import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";

import { useFormContext } from "react-hook-form";

import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { UTP2CustomQuestionTypes } from "../shared/enums/UTP2";
import { DeleteButton } from "../shared/FormLayout/IconButtons/DeleteButton";
import { EditButton } from "../shared/FormLayout/IconButtons/EditButton";
import MenuButton from "../shared/MenuButton";
import { ValidationMessage } from "../shared/ValidationMessage";

import { convertReusableQuestionToWorkflowQuestionField } from "../shared/helpers/CustomQuestionFieldUtils";
import { ReusableCustomQuestionField } from "../shared/interfaces/ReusableCustomQuestionField";
import { ReusableQuestionFieldSelectionDialog } from "./ReusableCustomQuestions/QuestionFields/ReusableQuestionFieldSelectionDialog";
import { CustomQuestionBuilderDialog } from "./WorkflowDetail/CustomQuestionBuilder/CustomQuestionBuilderDialog";
import { CustomQuestionField } from "../shared/interfaces/Workflow";
import { AccessControlContext } from "../contexts/AccessControlContext";

interface Props {
  customerId: number;
  groupId: string;
  groupFields?: CustomQuestionField[];
  editMode?: boolean;
  onQuestionFieldAdd?: (groupId: string, questionField: CustomQuestionField) => void;
  onQuestionFieldEdit?: (groupId: string, questionField: CustomQuestionField) => void;
  onQuestionFieldRemove?: (groupId: string, fieldId: string) => void;
}

export function CustomQuestionsList(props: Props) {
  const { t } = useTranslation();

  const accessControlContext = useContext(AccessControlContext);
  const canEditWorkflow = accessControlContext.canI("edit", "workflow", props.customerId);

  const [questionBuilderDialogOpen, setQuestionBuilderDialogOpen] = useState(false);
  const [customQuestionField, setCustomQuestionField] = useState<CustomQuestionField | undefined>();
  const [customQuestionLibrarySelectionDialogOpen, setCustomQuestionLibrarySelectionDialogOpen] = useState(false);

  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();

  register(`substationTab.customQuestionGroups.${props.groupId}.fields`, {
    value: props.groupFields,
    shouldUnregister: true,
    validate: (value) => value?.length > 0,
  });

  useEffect(() => {
    setValue(`substationTab.customQuestionGroups.${props.groupId}.fields`, props.groupFields);
  }, [props.groupId, props.groupFields, setValue]);

  function handleAddCustomQuestionButtonClick() {
    setCustomQuestionField(undefined);
    setQuestionBuilderDialogOpen(true);
  }

  function handleAddCustomQuestionFromLibraryButtonClick() {
    setCustomQuestionField(undefined);
    setCustomQuestionLibrarySelectionDialogOpen(true);
  }

  function handleConfirmAddQuestionFromLibraryClick(selectedQuestions: ReusableCustomQuestionField[]) {
    props.groupId &&
      selectedQuestions.forEach((selectedQuestion) => {
        const questionField = convertReusableQuestionToWorkflowQuestionField(selectedQuestion);
        props.onQuestionFieldAdd?.(props.groupId, questionField);
      });
  }

  function handleEditCustomQuestionButtonClick(questionField: CustomQuestionField) {
    setCustomQuestionField(questionField);
    setQuestionBuilderDialogOpen(true);
  }

  function handleConfirmAddCustomQuestion(questionField: CustomQuestionField) {
    props.groupId && props.onQuestionFieldAdd?.(props.groupId, questionField);
    setQuestionBuilderDialogOpen(false);
    setCustomQuestionField(undefined);
  }

  function handleConfirmEditCustomQuestion(questionField: CustomQuestionField) {
    props.groupId && props.onQuestionFieldEdit?.(props.groupId, questionField);
    setQuestionBuilderDialogOpen(false);
    setCustomQuestionField(undefined);
  }

  function handleCancelAddCustomQuestion() {
    setCustomQuestionField(undefined);
    setQuestionBuilderDialogOpen(false);
  }

  function handleRemoveCustomQuestion(fieldId?: string) {
    props.groupId && fieldId && props.onQuestionFieldRemove?.(props.groupId, fieldId);
  }

  return (
    <List>
      {props.groupFields && props.groupFields.length > 0 ? (
        props.groupFields.map((questionField: CustomQuestionField) => {
          return (
            <ListItem key={`listItem-${questionField.id}`}>
              {props.editMode && (
                <ListItemIcon>
                  <DeleteButton onClick={() => handleRemoveCustomQuestion(questionField.id)} />
                  <EditButton onClick={() => handleEditCustomQuestionButtonClick(questionField)} />
                </ListItemIcon>
              )}
              <ListItemText primary={questionField.name} />
              <ListItemSecondaryAction>
                {questionField.type === UTP2CustomQuestionTypes.Dropdown && (
                  <Dropdown
                    options={[...(questionField.options || []), t("customQuestions.other")]}
                    placeholder={t("customQuestions.dropdown")}
                  />
                )}
                {questionField.type === UTP2CustomQuestionTypes.ExclusiveDropdown && (
                  <Dropdown options={questionField.options || undefined} placeholder={t("customQuestions.dropdown")} />
                )}
                {questionField.type === UTP2CustomQuestionTypes.Date && (
                  <InputText disabled placeholder={t("customQuestions.dateInput")} />
                )}
                {questionField.type === UTP2CustomQuestionTypes.TextInput && (
                  <InputText disabled placeholder={t("customQuestions.textInput")} />
                )}
              </ListItemSecondaryAction>
            </ListItem>
          );
        })
      ) : (
        <>
          <Stack alignItems="center">
            <Typography variant="caption" noWrap>
              {t("customQuestions.customQuestionsEmpty")}{" "}
            </Typography>
            {errors?.substationTab?.customQuestionGroups?.[props.groupId]?.fields?.type === "validate" && (
              <ValidationMessage
                invalidMessage={t("customQuestions.customQuestionsEmptyValidation")}
                validationType="error"
                showIcon
              />
            )}
          </Stack>
        </>
      )}
      <Box display="flex" justifyContent="center">
        {props.editMode && (
          <MenuButton
            variant="iconButton"
            tooltipText={t("customQuestions.addQuestion")}
            menuItems={[
              {
                icon: <LibraryAddIcon />,
                text: t("customQuestionLibrary.addFromQuestionLibrary"),
                onClick: handleAddCustomQuestionFromLibraryButtonClick,
              },
              {
                icon: <AddIcon />,
                text: t("customQuestions.addNewQuestion"),
                onClick: handleAddCustomQuestionButtonClick,
              },
            ]}
          >
            <AddCircleIcon color="primary" />
          </MenuButton>
        )}
      </Box>
      {questionBuilderDialogOpen && (
        <CustomQuestionBuilderDialog
          customQuestionField={customQuestionField}
          open={questionBuilderDialogOpen}
          onConfirm={customQuestionField ? handleConfirmEditCustomQuestion : handleConfirmAddCustomQuestion}
          onCancel={handleCancelAddCustomQuestion}
        />
      )}
      {customQuestionLibrarySelectionDialogOpen && (
        <ReusableQuestionFieldSelectionDialog
          customerId={props.customerId}
          selectedQuestions={[]}
          onApplySelection={handleConfirmAddQuestionFromLibraryClick}
          open={customQuestionLibrarySelectionDialogOpen}
          onClose={() => setCustomQuestionLibrarySelectionDialogOpen(false)}
          canEditWorkflow={canEditWorkflow?.valueOf()}
        />
      )}
    </List>
  );
}
